import React, { useCallback, useMemo } from 'react';
import { useCreateAccountConfirmationPageContext } from '../contexts/CreateAccountConfirmationPageContext';
import { useCreateAccountConfirmationPageState } from '../../../../hooks';
import messages from '../messages/confirmationForm';
import { ConfirmationErrorCode, ConfirmationFieldError } from '../../../../types';
import { IntlShape, useIntl } from 'react-intl';
import {
  inHouseSocialConfirmation,
  showFieldErrors,
} from '../../../../actions/confirmation-actions';
import { useRecaptchaContext } from '../../../../contexts/recaptcha';
import { useAppDispatch } from '../../../../stores/redux.store';
import { ConfirmationFormData } from '../components/ConfirmationForm';
import { toChallengeRequest } from '../../../../utilities/recaptcha';
import { useRegisterComponent } from '../../../orchestrator';
import {
  EventAttributes,
  useAnalyticsClient,
} from '../../../../utilities/analytics/analytics-web-client';
import { CREATE_ACCOUNT_CONFIRMATION_PAGE_ID } from '../constants';
import { LoginType } from '../../../../utilities/social-login';
import { useNavigateSoftOrHard } from '../../../../utilities/hooks/useNavigateSoftOrHard';
import { useForceRedirect } from '../../../../utilities/hooks';
import { CookieMissingErrorMessage } from '../../../../components/CookieMissingErrorMessage';

export type ConfirmationErrorWithMessage = {
  code: ConfirmationErrorCode;
  message: React.ReactNode;
};

type UseCreateAccountConfirmationHookProps = {
  /**
   * Called when the user indicates their intention to submit (i.e. submit button click), pre-validation
   */
  onSubmit?: () => void;
  /**
   * Called upon (FE) validation failing, for any reason.
   *
   * @errors Object containing the errors that were found during validation.
   */
  onFailure?: (errors: ConfirmationFieldError) => void;
  /**
   * @deprecated Is not actually called, and no user currently provides it.
   *
   * @note This callback is _NOT_ called. Will be removed in future releases
   */
  onSuccess?: () => void;
  /**
   * Used to opt out of automatic redirects upon successful account creation.
   * Determines whether or not we redirect the user once we confirm the user successfully (via a call to SIS in `inHouseSocialConfirmation`)
   *
   * For certain cases (e.g. Loom during IDAC onboarding) we do not want the redirect the user
   * in the case of user confirmation, because they have an extra step in the user onboarding flow
   * (workspace creation) that requires us to prevent redirecting
   *
   * @default true
   * @deprecated
   * When submission is complete, the user will be redirected away from IDAC, via
   * a series of backend redirects, to their product destination with a valid session.
   *
   * We no longer support opting out of this behaviour, to preserve reliability and
   * integrity of user flows & session distribution.
   */
  autoRedirect?: boolean;
};

export const useCreateAccountConfirmation = (
  { onFailure, onSubmit, autoRedirect = true }: UseCreateAccountConfirmationHookProps = {
    autoRedirect: true,
  }
) => {
  useRegisterComponent(useCreateAccountConfirmation.name);

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigateSoftOrHard();

  const pageState = useCreateAccountConfirmationPageState();

  const {
    redirectTo,
    createNeeded,
    displayName,
    email,
    verifyNeeded,
    fieldErrors,
    formError,
    isLoading,
    loginType,
  } = pageState;
  const { fetchToken, recaptchaType } = useRecaptchaContext();

  const analyticsClient = useAnalyticsClient();

  // Prevent redirect if the autoRedirect flag is true
  // Used in cases where cobranding wants to redirect the user themselves.
  // NOTE: `autoRedirect` is deprecated, please do not use
  // @see: UseCreateAccountConfirmationHookProps['autoRedirect']
  useForceRedirect({ redirectTo: autoRedirect ? redirectTo : undefined });

  const { marketingConsent, privacyPolicyText = '' } = useCreateAccountConfirmationPageContext();

  const getEventAttributes = useCallback(
    (verificationCodeEntered: boolean): EventAttributes => {
      return {
        loginType: loginType,
        confirmationCreateAccountNeeded: createNeeded,
        confirmationVerifyEmailNeeded: verifyNeeded,
        verificationCodeEntered,
        formError,
        recaptchaType,
      };
    },
    [createNeeded, formError, loginType, recaptchaType, verifyNeeded]
  );

  const onValidationErrorAnalytics = useCallback(
    (errors: ConfirmationFieldError, verificationCodeEntered: boolean) => {
      if (errors.displayName) {
        const errorBooleanMap = Object.keys(errors).reduce(
          (acc: Record<string, boolean>, val) => ({ ...acc, [val + 'Error']: true }),
          {}
        );

        analyticsClient.uiEvent({
          page: CREATE_ACCOUNT_CONFIRMATION_PAGE_ID,
          action: 'shown',
          subject: 'error',
          subjectId: 'confirmCreateAccountIdpNoName',
          attributes: {
            errors: errorBooleanMap,
          },
        });
      }

      if (errors.code === 'empty_code') {
        analyticsClient.errorShownEvent(
          CREATE_ACCOUNT_CONFIRMATION_PAGE_ID,
          'emptyCode',
          getEventAttributes(verificationCodeEntered)
        );
      } else if (errors.code === 'invalid_length') {
        analyticsClient.errorShownEvent(
          CREATE_ACCOUNT_CONFIRMATION_PAGE_ID,
          'invalidCodeLength',
          getEventAttributes(verificationCodeEntered)
        );
      }
    },
    [analyticsClient, getEventAttributes]
  );

  const onSubmitAnalytics = useCallback(
    (verificationCodeEntered: boolean) => {
      analyticsClient.checkboxChangedEvent(
        CREATE_ACCOUNT_CONFIRMATION_PAGE_ID,
        'marketingConsent',
        {
          ...getEventAttributes(verificationCodeEntered),
          status: marketingConsent?.state,
        }
      );

      analyticsClient.formSubmittedEvent(
        CREATE_ACCOUNT_CONFIRMATION_PAGE_ID,
        'confirmCreateAccountIdp',
        getEventAttributes(verificationCodeEntered)
      );
    },
    [analyticsClient, getEventAttributes, marketingConsent?.state]
  );

  const handleSubmit = useCallback(
    (confirmationData: ConfirmationFormData) => {
      navigate(location.pathname + location.search + location.hash);
      const challengeRequest = toChallengeRequest(confirmationData.challengeResponse);

      const { loginType, hashedCsrfToken, confirmationToken, mfaToken } = pageState;
      dispatch(
        inHouseSocialConfirmation({
          ...confirmationData,
          privacyPolicyText,
          hashedCsrfToken,
          mfaToken,
          confirmationToken,
          loginType,
          challengeRequest,
        })
      );
    },
    [navigate, pageState, dispatch, privacyPolicyText]
  );

  const onCreateAccountConfirmation = useCallback(
    async ({ displayName, code }: { displayName?: string; code?: string }) => {
      const validationErrors: ConfirmationFieldError = {};

      if (isLoading) {
        return;
      }

      if (createNeeded && (!displayName || displayName.trim() === '')) {
        validationErrors.displayName = formatMessage(messages.displayNameEmpty); // TODO: do we want to create message codes here like in the OTP page?
      }

      if (verifyNeeded) {
        if (!code || code.trim() === '') {
          validationErrors.code = 'empty_code';
        } else if (code.trim().length < 7) {
          // Codes could be length 8 or 16, see HOT-94320 changes.
          // Condition of < 7 to allow for flexibility if further code length changes are needed.
          validationErrors.code = 'invalid_length';
        }
      }

      const verificationCodeEntered = Boolean(code);
      if (Object.keys(validationErrors).length) {
        dispatch(showFieldErrors(validationErrors));
        onValidationErrorAnalytics(validationErrors, verificationCodeEntered);

        onFailure?.(validationErrors);
        return;
      }

      const challengeResponse = await fetchToken();
      onSubmitAnalytics(verificationCodeEntered);
      onSubmit?.();

      if (createNeeded && verifyNeeded) {
        handleSubmit({
          displayName,
          code,
          marketingConsentState: marketingConsent,
          challengeResponse,
        });
      } else if (createNeeded) {
        handleSubmit({
          displayName,
          marketingConsentState: marketingConsent,
          challengeResponse,
        });
      } else {
        handleSubmit({
          code,
          challengeResponse,
        });
      }
    },
    [
      isLoading,
      createNeeded,
      verifyNeeded,
      fetchToken,
      onSubmitAnalytics,
      onSubmit,
      formatMessage,
      dispatch,
      onValidationErrorAnalytics,
      onFailure,
      handleSubmit,
      marketingConsent,
    ]
  );

  const canEditDisplayName = useMemo(() => {
    return (
      loginType === LoginType.Apple ||
      loginType === LoginType.Slack ||
      loginType === LoginType.Microsoft
    );
  }, [loginType]);

  const formErrorWithMessage: ConfirmationErrorWithMessage | undefined = useMemo(() => {
    if (!formError) return;

    const message = errorMessageFromCode(formError, formatMessage);

    return {
      code: formError,
      message,
    };
  }, [formError, formatMessage]);

  return {
    createAccountNeeded: createNeeded,
    canEditDisplayName,
    displayName,
    verifyEmailNeeded: verifyNeeded,
    onCreateAccountConfirmation,
    email,
    fieldErrors,
    formError: formErrorWithMessage,
    isLoading,
    redirectTo,
  };
};

const errorMessageFromCode = (
  errorCode: ConfirmationErrorCode,
  formatMessage: IntlShape['formatMessage']
) => {
  switch (errorCode) {
    case 'captcha_not_loaded':
    case 'captcha_failed':
      return formatMessage({
        id: 'confirmation.error.captcha.failed',
        defaultMessage:
          'We are having trouble verifying ReCAPTCHA for this request. Please try again. If the problem persists, try another browser/device or reach out to Atlassian Support.',
      });
    case 'captchaScoreTooLow':
      return formatMessage({
        id: 'confirmation.error.captcha.score_too_low',
        defaultMessage:
          "We'd like to make sure it's really you creating your account. Please confirm you're not a robot.",
        description: 'Message asking user to tick the reCAPTCHA checkbox and resubmit the form',
      });
    case 'cookieMissing':
      return <CookieMissingErrorMessage />;
    case 'request_error':
      return formatMessage({
        id: 'confirmation.error.offline',
        defaultMessage:
          'We seem to have lost the connection. Check your network settings, then try again. ',
      });
    case 'not_allowed':
      return formatMessage({
        id: 'confirmation.error.not_allowed',
        defaultMessage:
          "We're unable to create your account at this time. If you believe this is an error, please contact Atlassian Support.",
      });
    default:
      return formatMessage({
        id: 'confirmation.error.unknown',
        defaultMessage:
          'Something went wrong while trying to create your account. Wait a few moments, then try again.',
      });
  }
};
